import React, { useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { fetchDocumentsApi, uploadEvidenceDocumentApi } from "../lib/api";

export default function EvidenceReferenceField({
  label = "Evidence",
  value = "",
  onChange,
  disabled = false,
  helpText = "Use a URL, an existing filename, or upload a file to generate a platform reference.",
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [docQuery, setDocQuery] = useState("");
  const [documents, setDocuments] = useState([]);

  const filteredDocuments = useMemo(() => {
    const q = String(docQuery || "").trim().toLowerCase();
    const list = Array.isArray(documents) ? documents : [];
    const sorted = [...list].sort((a, b) =>
      String(b?.uploaded_at || "").localeCompare(String(a?.uploaded_at || ""))
    );
    if (!q) return sorted.slice(0, 12);
    return sorted
      .filter((doc) => String(doc?.filename || doc?.id || "").toLowerCase().includes(q))
      .slice(0, 12);
  }, [documents, docQuery]);

  const loadDocuments = async () => {
    if (loadingDocs || documents.length) return;
    try {
      setLoadingDocs(true);
      const docs = await fetchDocumentsApi();
      setDocuments(Array.isArray(docs) ? docs : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load documents");
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleUploadClick = () => {
    if (disabled || uploading) return;
    fileInputRef.current?.click();
  };

  const handlePickerToggle = async () => {
    if (disabled || uploading) return;
    const nextOpen = !pickerOpen;
    setPickerOpen(nextOpen);
    if (nextOpen) await loadDocuments();
  };

  const handleSelectDocument = (doc) => {
    const filename = String(doc?.filename || doc?.id || "").trim();
    if (!filename) return;
    if (typeof onChange === "function") onChange(`document:${filename}`);
    setPickerOpen(false);
    setDocQuery("");
    toast.success("Evidence linked");
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      setUploading(true);
      const resp = await uploadEvidenceDocumentApi(file);
      const reference = String(resp?.reference || "").trim();
      if (!reference) throw new Error("Evidence upload did not return a reference");
      if (typeof onChange === "function") onChange(reference);
      toast.success("Evidence uploaded");
    } catch (err) {
      toast.error(err?.message || "Failed to upload evidence");
    } finally {
      setUploading(false);
    }
  };

  return (
    <label className="settings-field" style={{ gridColumn: "1 / -1" }}>
      <span>{label}</span>
      <div className="evidence-field-row">
        <input
          value={value}
          onChange={(e) => (typeof onChange === "function" ? onChange(e.target.value) : null)}
          disabled={disabled || uploading}
          placeholder="https://... or document:filename.pdf"
        />
        <button
          className="btn-secondary evidence-upload-btn"
          type="button"
          onClick={handlePickerToggle}
          disabled={disabled || uploading}
        >
          {loadingDocs ? "Loading..." : pickerOpen ? "Hide documents" : "Choose document"}
        </button>
        <button
          className="btn-secondary evidence-upload-btn"
          type="button"
          onClick={handleUploadClick}
          disabled={disabled || uploading}
        >
          {uploading ? "Uploading..." : "Upload evidence"}
        </button>
      </div>
      {pickerOpen && (
        <div className="evidence-picker-panel">
          <input
            value={docQuery}
            onChange={(e) => setDocQuery(e.target.value)}
            disabled={disabled || loadingDocs}
            placeholder="Search existing documents..."
          />
          <div className="evidence-picker-list">
            {filteredDocuments.map((doc) => {
              const key = String(doc?.id || doc?.filename || "");
              const filename = String(doc?.filename || doc?.id || "");
              const uploadedAt = String(doc?.uploaded_at || "").trim();
              return (
                <button
                  key={key}
                  className="evidence-picker-item"
                  type="button"
                  onClick={() => handleSelectDocument(doc)}
                >
                  <span className="evidence-picker-name">{filename}</span>
                  <span className="evidence-picker-meta">{uploadedAt || "Uploaded date unavailable"}</span>
                </button>
              );
            })}
            {!loadingDocs && filteredDocuments.length === 0 && (
              <div className="evidence-picker-empty">No matching documents found.</div>
            )}
            {loadingDocs && <div className="evidence-picker-empty">Loading documents...</div>}
          </div>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={handleFileChange}
        disabled={disabled || uploading}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.zip"
      />
      <span className="evidence-help-text">{helpText}</span>
    </label>
  );
}
